// Bank to Bank Transfer verification (legacy /AccountsApproval/VerifyBankTransfer).
// Pending rows are verified/approved via the shared status list; rows returned for update
// (Approvalstatus '0') get legacy's resubmit form (To Bank, Date, Remarks, Amount editable).
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { ArrowRightLeft, Clock, ArrowRight, RotateCcw, Send } from 'lucide-react';

import InboxHeader from '../../components/Inbox/InboxHeader';
import ActionButtons from '../../components/Inbox/ActionButtons';
import RemarksHistory from '../../components/Inbox/RemarksHistory';
import InboxSplitLayout from '../../components/Inbox/InboxSplitLayout';
import { SimpleCommentInput } from '../../components/Inbox/VerificationInput';
import { formatIndianCurrency } from '../../utilities/amountToTextHelper';

import {
    fetchVerifyBankTransferQueue,
    fetchVerifyBankTransferDetail,
    fetchReturnedBankTransferDetail,
    fetchTransferBanks,
    submitVerifyBankTransfer,
    submitUpdateBankTransfer,
    clearVerifyBankTransferDetail,
    resetVerifyBankTransfer,
    selectVBTQueue, selectVBTQueueLoading, selectVBTQueueError,
    selectVBTDetail, selectVBTDetailLoading, selectVBTDetailError,
    selectVBTBanks, selectVBTVerifyLoading, selectVBTUpdateLoading,
} from '../../slices/accountsSlice/verifyBankTransferSlice';

import {
    fetchStatusList,
    selectEnabledActions,
    selectHasActions,
    selectStatusListLoading,
    selectStatusListError,
    resetApprovalData,
    setShowReturnButton,
} from '../../slices/CommonSlice/getStatusSlice';

import {
    fetchRemarks,
    selectRemarks,
    selectRemarksLoading,
    setSelectedMOID,
} from '../../slices/supplierPOSlice/purcahseHelperSlice';

const ACTION_VERB = {
    Verify: 'Verified Successfully',
    Approve: 'Approved Successfully',
    Return: 'Returned for Update',
    Reject: 'Rejected Successfully',
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const Field = ({ label, value, className = '' }) => (
    <div className={className}>
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">{label}</p>
        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 break-words">{value || value === 0 ? value : '—'}</p>
    </div>
);

const StatusBadge = ({ status }) => (String(status) === '0' ? (
    <span className="px-2 py-0.5 text-xs rounded-full border bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 whitespace-nowrap">Return</span>
) : (
    <span className="px-2 py-0.5 text-xs rounded-full border bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 whitespace-nowrap">Verification Pending</span>
));

const inputCls = 'w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500';
const labelCls = 'block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1';

const formatAmount = (v) => (v != null && v !== '' ? `₹ ${formatIndianCurrency(v)}` : null);
const todayIso = () => new Date().toISOString().slice(0, 10);
// "25-Sep-2026" → "2026-09-25" (for <input type="date">)
const legacyToIso = (v) => {
    const m = /^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/.exec(String(v || '').trim());
    if (!m) return '';
    const month = MONTHS.findIndex((x) => x.toLowerCase() === m[2].toLowerCase()) + 1;
    return month ? `${m[3]}-${String(month).padStart(2, '0')}-${m[1].padStart(2, '0')}` : '';
};
// "2026-09-25" → "25-Sep-2026" — legacy's datepicker format. The SP takes @Date as DATETIME from a
// string, and dd-MMM-yyyy is unambiguous regardless of the server's DATEFORMAT.
const isoToLegacy = (v) => {
    const [y, mo, d] = String(v || '').split('-');
    return y && mo && d ? `${d}-${MONTHS[Number(mo) - 1]}-${y}` : '';
};

const VerifyBankTransfer = ({ notificationData, onNavigate }) => {
    const dispatch = useDispatch();
    const { userData, userDetails } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID;

    const queue = useSelector(selectVBTQueue);
    const queueLoading = useSelector(selectVBTQueueLoading);
    const queueError = useSelector(selectVBTQueueError);
    const detail = useSelector(selectVBTDetail);
    const detailLoading = useSelector(selectVBTDetailLoading);
    const detailError = useSelector(selectVBTDetailError);
    const banks = useSelector(selectVBTBanks);
    const verifyLoading = useSelector(selectVBTVerifyLoading);
    const updateLoading = useSelector(selectVBTUpdateLoading);

    const statusLoading = useSelector(selectStatusListLoading);
    const statusError = useSelector(selectStatusListError);
    const enabledActions = useSelector(selectEnabledActions);
    const hasActions = useSelector(selectHasActions);

    const remarks = useSelector(selectRemarks);
    const remarksLoading = useSelector(selectRemarksLoading);

    const { InboxTitle, ModuleDisplayName } = notificationData || {};

    const [selectedItem, setSelectedItem] = useState(null);
    const [comment, setComment] = useState('');
    const [showRemarksHistory, setShowRemarksHistory] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
    const [isLeftPanelHovered, setIsLeftPanelHovered] = useState(false);
    // Returned-row resubmit form
    const [updToBankId, setUpdToBankId] = useState('');
    const [updDate, setUpdDate] = useState('');
    const [updRemarks, setUpdRemarks] = useState('');
    const [updAmount, setUpdAmount] = useState('');

    const getCurrentUser = () => userData?.userName || userDetails?.userName || 'system';

    const isReturned = String(selectedItem?.Approvalstatus) === '0';
    // Only trust the detail that belongs to the row currently selected.
    const transfer = selectedItem && String(detail?.Refno) === String(selectedItem.Refno) ? detail : null;

    useEffect(() => {
        if (roleId) dispatch(fetchVerifyBankTransferQueue(roleId));
    }, [roleId, dispatch]);

    useEffect(() => {
        // Legacy's status dropdown on this page keeps every option, including "Return".
        dispatch(setShowReturnButton('Yes'));
        return () => {
            dispatch(resetVerifyBankTransfer());
            dispatch(resetApprovalData());
        };
    }, [dispatch]);

    useEffect(() => {
        if (!selectedItem) return;
        dispatch(clearVerifyBankTransferDetail());
        dispatch(resetApprovalData());
        dispatch(setShowReturnButton('Yes'));
        if (String(selectedItem.Approvalstatus) === '0') {
            dispatch(fetchReturnedBankTransferDetail(selectedItem.Refno));
            if (!banks.length) dispatch(fetchTransferBanks());
        } else {
            dispatch(fetchVerifyBankTransferDetail(selectedItem.Refno));
            // The queue row already carries MOID, so the action list loads alongside the detail.
            if (selectedItem.MOID && roleId) {
                dispatch(fetchStatusList({ MOID: selectedItem.MOID, ROID: roleId, ChkAmt: 0 }));
            }
        }
        if (selectedItem.MOID) {
            dispatch(setSelectedMOID(selectedItem.MOID));
            // spVerifybanktobankdeposit writes Remarks.TransactionNo = the transfer's ref no.
            dispatch(fetchRemarks({ trno: selectedItem.Refno, moid: selectedItem.MOID }));
        }
        setComment('');
        setShowRemarksHistory(false);
        setIsLeftPanelCollapsed(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedItem]);

    // Prefill the resubmit form once the returned transfer is in.
    useEffect(() => {
        if (!isReturned || !transfer) return;
        setUpdToBankId(transfer.ToId ? String(transfer.ToId) : '');
        setUpdDate(legacyToIso(transfer.Date));
        setUpdRemarks(transfer.Remarks || '');
        setUpdAmount(transfer.Amount != null ? String(transfer.Amount) : '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [transfer?.Refno]);

    const handleBackToInbox = () => {
        if (onNavigate) onNavigate('dashboard', { name: 'Dashboard', type: 'dashboard' });
    };

    const handleRefresh = () => {
        if (roleId) dispatch(fetchVerifyBankTransferQueue(roleId));
    };

    const finishAndRefresh = () => {
        setTimeout(() => {
            dispatch(fetchVerifyBankTransferQueue(roleId));
            setSelectedItem(null);
            setComment('');
            setShowRemarksHistory(false);
            setIsLeftPanelCollapsed(false);
            dispatch(clearVerifyBankTransferDetail());
            dispatch(resetApprovalData());
        }, 800);
    };

    const handleActionClick = async (action) => {
        if (!transfer) { toast.error('No bank transfer selected'); return; }
        if (!comment.trim()) { toast.error('Enter Note'); return; }

        const actionValue = action.type;
        const payload = {
            Refno: transfer.Refno,
            Approvalstatus: actionValue,
            ApprovalRemarks: comment.trim(),
            Createdby: getCurrentUser(),
            RoleID: roleId,
        };

        try {
            const result = await dispatch(submitVerifyBankTransfer(payload)).unwrap();
            const status = typeof result === 'string' ? result : result?.Data;
            if (status === 'Submitted' || status === 'Submited') {
                toast.success(`Bank Transfer ${ACTION_VERB[actionValue] || `${actionValue}d`}`);
                finishAndRefresh();
                return;
            }
            toast.error(status || `${actionValue} was not applied — the server returned no confirmation.`);
        } catch (error) {
            toast.error(typeof error === 'string' ? error : 'Failed to submit bank transfer verification');
        }
    };

    const handleAmountChange = (e) => {
        const value = e.target.value;
        if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) setUpdAmount(value);
    };

    const handleResubmit = async () => {
        if (!transfer) return;
        const errors = [];
        if (!updToBankId) errors.push('Select To Bank');
        if (updToBankId && transfer.FromId && String(transfer.FromId) === String(updToBankId)) {
            errors.push('From Bank and To Bank should not be the same');
        }
        if (!updAmount || parseFloat(updAmount) === 0) errors.push('Enter Amount');
        if (!updRemarks.trim()) errors.push('Enter Remarks');
        if (!updDate) errors.push('Select Date');
        if (errors.length) { errors.forEach((m) => toast.error(m)); return; }

        const payload = {
            Refno: transfer.Refno,
            From: transfer.FromId,
            To: updToBankId,
            Date: isoToLegacy(updDate),
            Remarks: updRemarks.trim(),
            Amount: parseFloat(updAmount),
            Createdby: getCurrentUser(),
            RoleID: roleId,
        };

        try {
            const result = await dispatch(submitUpdateBankTransfer(payload)).unwrap();
            const status = typeof result === 'string' ? result : result?.Data;
            // SPBankToBankReturnTransfer's own success literal is "Successfull".
            if (status === 'Successfull' || status === 'Submitted') {
                toast.success('Bank Transfer Updated Successfully');
                finishAndRefresh();
                return;
            }
            toast.error(status || 'Error Occurred While Return Bank Transfer Updation');
        } catch (error) {
            toast.error(typeof error === 'string' ? error : 'Error Occurred While Return Bank Transfer Updation');
        }
    };

    const filteredQueue = queue.filter((item) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return String(item.Refno ?? '').toLowerCase().includes(q)
            || item.FromBank?.toLowerCase().includes(q)
            || item.ToBank?.toLowerCase().includes(q)
            || item.Date?.toLowerCase().includes(q)
            || String(item.Amount ?? '').includes(q);
    });

    const renderItemCard = (item) => (
        <div className="p-4">
            <div className="flex items-center space-x-3 mb-2">
                <div className="w-12 h-12 rounded-full border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-indigo-100 dark:from-indigo-800/50 dark:to-indigo-800/50 flex items-center justify-center">
                    <ArrowRightLeft className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm truncate">Ref #{item.Refno}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{item.Date}</p>
                </div>
                <span className="text-sm font-bold text-gray-900 dark:text-white whitespace-nowrap">{formatAmount(item.Amount)}</span>
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                <div className="flex items-center gap-1 min-w-0">
                    <span className="truncate">{item.FromBank}</span>
                    <ArrowRight className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{item.ToBank}</span>
                </div>
                <div className="flex justify-end"><StatusBadge status={item.Approvalstatus} /></div>
            </div>
        </div>
    );

    const renderListItem = (item) => (
        <div className="flex items-center gap-x-6 gap-y-1 flex-wrap text-sm">
            <span className="font-mono bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded text-xs">{item.Refno}</span>
            <span className="text-gray-500 dark:text-gray-400 min-w-[100px]">{item.Date}</span>
            <span className="font-semibold text-gray-900 dark:text-white truncate max-w-[220px]">{item.FromBank}</span>
            <ArrowRight className="w-3 h-3 text-gray-400" />
            <span className="font-semibold text-gray-900 dark:text-white truncate max-w-[220px]">{item.ToBank}</span>
            <span className="font-semibold text-gray-900 dark:text-white">{formatAmount(item.Amount)}</span>
            <span className="ml-auto"><StatusBadge status={item.Approvalstatus} /></span>
        </div>
    );

    const renderCollapsedItem = () => (
        <div className="w-full h-full rounded-lg border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-indigo-100 dark:from-indigo-800/50 dark:to-indigo-800/50 flex items-center justify-center">
            <ArrowRightLeft className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
        </div>
    );

    const renderPendingDetails = () => (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border-2 border-gray-200 dark:border-gray-700 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Field label="Date" value={transfer.Date} />
                <Field label="From Bank" value={transfer.FromBank} />
                <Field label="Mode of Pay" value={transfer.ModeofPay} />
                <Field label="No" value={transfer.No} />
                <Field label="To Bank" value={transfer.ToBank} />
                <Field label="Amount" value={formatAmount(transfer.Amount)} />
                <Field label="Amount in Words" value={transfer.AmountInWords} className="sm:col-span-2" />
                <Field label="Remarks" value={transfer.Remarks} className="sm:col-span-2 lg:col-span-4" />
            </div>
        </div>
    );

    const renderResubmitForm = () => (
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-2xl p-5 border-2 border-amber-200 dark:border-amber-700 space-y-4">
            <div className="flex items-center gap-2">
                <RotateCcw className="w-4 h-4 text-amber-700 dark:text-amber-400" />
                <h3 className="text-sm font-bold text-amber-800 dark:text-amber-300">Returned for Update — correct and resubmit</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Field label="From Bank" value={transfer.From} />
                <Field label="Mode of Pay" value={transfer.Mode_of_Pay} />
                <Field label="No" value={transfer.No} />
                <div>
                    <label className={labelCls}>To Bank <span className="text-red-500">*</span></label>
                    <select className={inputCls} value={updToBankId} onChange={(e) => setUpdToBankId(e.target.value)}>
                        <option value="">-Please Select-</option>
                        {banks.map((b) => <option key={b.Bank_Id} value={b.Bank_Id}>{b.Bank_Name}</option>)}
                    </select>
                </div>
                <div>
                    <label className={labelCls}>Date <span className="text-red-500">*</span></label>
                    <input type="date" className={inputCls} value={updDate} max={todayIso()} onChange={(e) => setUpdDate(e.target.value)} />
                </div>
                <div>
                    <label className={labelCls}>Amount <span className="text-red-500">*</span></label>
                    <input type="text" inputMode="decimal" className={inputCls} value={updAmount} onChange={handleAmountChange} />
                </div>
                <div className="sm:col-span-3">
                    <label className={labelCls}>Remarks <span className="text-red-500">*</span></label>
                    <textarea rows={3} className={inputCls} value={updRemarks} onChange={(e) => setUpdRemarks(e.target.value)} />
                </div>
            </div>
            <div className="flex justify-end">
                <button
                    type="button"
                    onClick={handleResubmit}
                    disabled={updateLoading}
                    className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                    {updateLoading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : <Send className="w-4 h-4" />}
                    Resubmit
                </button>
            </div>
        </div>
    );

    const renderActions = () => {
        if (statusLoading) {
            return (
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-center space-x-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                        <span className="text-gray-600 dark:text-gray-400">Loading actions…</span>
                    </div>
                </div>
            );
        }
        if (statusError) {
            return (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-700">
                    <p className="text-red-600 dark:text-red-400 text-center">⚠️ Error loading actions: {statusError}</p>
                </div>
            );
        }
        if (!hasActions || !enabledActions || enabledActions.length === 0) {
            return (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-6 border border-yellow-200 dark:border-yellow-700">
                    <p className="text-yellow-700 dark:text-yellow-400 text-center">ℹ️ No actions available for this bank transfer</p>
                </div>
            );
        }
        return (
            <ActionButtons
                actions={enabledActions}
                onActionClick={handleActionClick}
                loading={verifyLoading}
                isVerified={true}
                comment={comment}
                showValidation={true}
            />
        );
    };

    const renderDetailContent = () => {
        if (!selectedItem) return null;

        if (detailLoading || (!transfer && !detailError)) {
            return (
                <div className="flex items-center justify-center gap-3 py-12">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Loading bank transfer details…</span>
                </div>
            );
        }
        if (detailError || !transfer) {
            return (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-700">
                    <p className="text-red-600 dark:text-red-400 text-center">⚠️ {detailError || 'Bank transfer details could not be loaded — it may no longer be pending at your level.'}</p>
                </div>
            );
        }

        return (
            <div className="space-y-6">
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-2xl p-6 border-2 border-indigo-200 dark:border-indigo-700">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1 flex items-center gap-2 flex-wrap">
                        <span>{selectedItem.FromBank}</span>
                        <ArrowRight className="w-5 h-5 text-indigo-500" />
                        <span>{selectedItem.ToBank}</span>
                    </h2>
                    <p className="text-indigo-600 dark:text-indigo-400 font-semibold mb-3">{formatAmount(selectedItem.Amount)}</p>
                    <div className="flex flex-wrap gap-2">
                        <span className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full text-xs font-medium">Ref No: {selectedItem.Refno}</span>
                        <span className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-xs font-medium">Date: {selectedItem.Date}</span>
                        <StatusBadge status={selectedItem.Approvalstatus} />
                    </div>
                </div>

                {isReturned ? null : renderPendingDetails()}

                <RemarksHistory
                    isOpen={showRemarksHistory}
                    onToggle={() => setShowRemarksHistory(!showRemarksHistory)}
                    remarks={remarks}
                    loading={remarksLoading}
                    title="Approval History"
                />

                {isReturned ? renderResubmitForm() : (
                    <>
                        <SimpleCommentInput
                            comment={comment}
                            onCommentChange={(e) => setComment(e.target.value)}
                            label="Notes"
                            placeholder="Enter verification notes…"
                            required
                            rows={3}
                            showCharCount
                        />
                        {renderActions()}
                    </>
                )}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <InboxHeader
                title={`${InboxTitle || 'Bank Transfer Verification'} (${queue.length})`}
                subtitle={ModuleDisplayName}
                itemCount={queue.length}
                onBackClick={handleBackToInbox}
                HeaderIcon={ArrowRightLeft}
                badgeText="Bank Transfer Verification"
                badgeCount={queue.length}
                searchConfig={{
                    enabled: true,
                    placeholder: 'Search by ref no, bank, date, amount…',
                    value: searchQuery,
                    onChange: (e) => setSearchQuery(e.target.value),
                }}
                enableViewToggle
            />

            <InboxSplitLayout
                isLeftPanelCollapsed={isLeftPanelCollapsed}
                onLeftPanelCollapseToggle={setIsLeftPanelCollapsed}
                isLeftPanelHovered={isLeftPanelHovered}
                onLeftPanelHoverChange={setIsLeftPanelHovered}
                left={{
                    items: filteredQueue,
                    selectedItem,
                    onItemSelect: setSelectedItem,
                    renderItem: renderItemCard,
                    renderListItem,
                    renderCollapsedItem,
                    loading: queueLoading,
                    error: queueError,
                    onRefresh: handleRefresh,
                    config: {
                        title: 'Pending',
                        icon: Clock,
                        emptyMessage: 'No bank transfers pending verification!',
                        itemKey: 'Refno',
                        enableCollapse: true,
                        enableRefresh: true,
                        enableHover: true,
                        maxHeight: '100%',
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                    },
                    renderPopupContent: () => renderDetailContent(),
                    popupConfig: {
                        title: 'Bank Transfer Verification',
                        icon: ArrowRightLeft,
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                        maxWidth: 'max-w-[80vw]',
                    },
                }}
                right={{
                    selectedItem,
                    loading: false,
                    renderContent: renderDetailContent,
                    config: {
                        title: 'Bank Transfer Details',
                        icon: ArrowRightLeft,
                        selectedTitle: 'Bank Transfer Verification',
                        emptyTitle: 'No Bank Transfer Selected',
                        emptyMessage: 'Select a bank transfer from the list to view details and take action.',
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                        maxHeight: 'calc(100vh-200px)',
                        sticky: true,
                        stickyTop: '1.5rem',
                    },
                }}
            />
        </div>
    );
};

export default VerifyBankTransfer;
